import React, { useEffect, useRef, useState } from "react";
import Drawer from "../../../drawer/Drawer";
import Tabs from "../../../elements/Tabs";
import SelectedEventsList from "./SelectedEventsList";
import EventList from "./EventList";
import Button from "../../../form/Button";
import { useSelector } from "react-redux";
import { useOrganization } from "../../../../context/OrganizationContext";
import { useLocation } from "react-router-dom";
import { useDebounce } from "../../../../hooks/useDebounce";
import { eventList } from "../../../../services/eventService";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Search from "../../../form/Search";
import { elementResize, loadDataOnScroll } from "../../../../helpers";
import Checkbox from "../../../form/Checkbox";
import { useTranslation } from "react-i18next";

const EventsFilterDrawer = ({
  open,
  close = () => {},
  selectedEvents = [],
  setSelectedEvents = () => {},
}) => {
  const divRef = useRef(null);
  const [divHeight, setDivHeight] = useState(0);
  const { t } = useTranslation();
  const [selected, setSelected] = useState([]);
  const { organizations, selectedOrganization } = useOrganization();
  const { lang } = useSelector((state) => state?.langProvider);
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "ordering",
  });

  useEffect(() => {
    elementResize(divRef, (data) => {
      setDivHeight(data?.height);
    });
  }, []);

  const {
    data: eventsData,
    isLoading: eventLoading,
    isFetching: eventFetching,
  } = useQuery({
    enabled: !!selectedOrganization,
    queryKey: [
      "Event-reporting",
      { ...filters, limit: Math.ceil(divHeight / 83) * 2 + 1 },
      search,
      selectedOrganization,
    ],
    queryFn: async () => {
      const res = await eventList({
        organization: selectedOrganization,
        ...filters,
        ...(search && { keyword: search }),
        limit: Math.ceil(divHeight / 83) * 2 + 1,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      if (filters?.page > 1) {
        return {
          ...res?.docs,
          data: [...(eventsData?.data || []), ...res?.docs?.data]?.filter(
            (item, index, self) =>
              index === self.findIndex((t) => t?._id === item?._id)
          ),
        };
      } else {
        return { ...res?.docs };
      }
    },
    placeholderData: keepPreviousData,
  });

  const tabItems = [
    {
      label: (
        <div>
          {t("Events")} ({eventsData?.metadata?.totalDocs || 0})
        </div>
      ),
      content: (
        <div className="h-full">
          <Search
            search={setKeyword}
            searchValue={keyword}
            placeholder={t("Search here")}
          />
          <div className="flex items-center gap-2 my-3">
            <Checkbox
              onChange={(e) => {
                if (e?.target?.checked) {
                  setSelected(eventsData?.data);
                } else {
                  setSelected([]);
                }
              }}
              checked={
                eventsData?.data?.length > 0 &&
                eventsData?.data?.every((ev) =>
                  selected?.some((item) => item?._id === ev?._id)
                )
              }
              checkboxClass="ml-3"
            />
            <span className="font-medium text-sm">{t("Select all")}</span>
          </div>
          <div
            ref={divRef}
            className="h-[calc(100%-65px)] overflow-y-auto"
            onScroll={(e) => {
              loadDataOnScroll({
                event: e,
                metadata: eventsData?.metadata,
                setData: setFilters,
              });
            }}
          >
            <EventList
              loading={eventLoading}
              selectedEvents={selected}
              setSelectedEvents={(val) => {
                if (selected?.some((item) => item?._id === val?._id)) {
                  setSelected(selected?.filter((e) => e?._id !== val?._id));
                } else {
                  setSelected((pre) => [...pre, val]);
                }
              }}
              eventsData={eventsData}
            />
          </div>
        </div>
      ),
    },
    {
      label: (
        <div>
          {t("Selected")} ({selected?.length || 0})
        </div>
      ),
      content: (
        <SelectedEventsList
          selectedEvents={selected}
          setSelectedEvents={(val) => {
            if (selected?.some((item) => item?._id === val?._id)) {
              setSelected(selected?.filter((e) => e?._id !== val?._id));
            } else {
              setSelected((pre) => [...pre, val]);
            }
          }}
        />
      ),
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={(val) => {
        close(val);
      }}
      actualSize={"md"}
      size={"md"}
      title={t("Select events")}
      subtitle={false}
      drawerBodyClass="p-0 max-h-[calc(100%-61px)]"
    >
      <Tabs
        items={tabItems}
        className={"max-h-[calc(100%-70px)] [&>div]:h-full overflow-hidden"}
        contentClass={"w-full px-5 h-[calc(100%-104px)]"}
        tabsClass="gap-3 p-5 pb-0 flex items-center gap-4"
        tabClass={
          "h-11 rounded-full bg-transparent border border-gray-200 flex items-center justify-center !text-deep-purple p-5 text-base capitalize font-normal"
        }
        activeTabClass={"bg-primary border border-primary !text-white"}
      />
      <div className="mt-auto flex items-center px-2 gap-2 bg-white py-3 relative z-[1]">
        <Button
          buttonClasses={
            "bg-transparent text-deep-purple border-deep-purple w-full"
          }
          buttonLabel={t("Cancle")}
          buttonFunction={close}
        />
        <Button
          buttonClasses={"w-full"}
          buttonLabel={t("Run report")}
          buttonFunction={() => {
            setSelectedEvents(selected);
            close();
          }}
        />
      </div>
    </Drawer>
  );
};

export default EventsFilterDrawer;
